import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';

import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import type { ChangeEvent } from 'react';

import { DataSetEnum } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';

import { Loading } from '../../components/Loading';
import { userFriendlyErrors } from '../../utils/back-errors.util';
import type { BackError } from '../../utils/back-errors.util';
import { MessageList } from '../../components/MessageList';

import { useRunFixturesMutation } from './hooks/useRunFixturesMutation.hook';

interface Props {
  readonly disabled: boolean;
}

export const DataSetLoadingForm = ({ disabled }: Readonly<Props>) => {
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: runFixtures, isPending } = useRunFixturesMutation();

  const [dataSet, setDataSet] = useState<DataSetEnum | typeof EMPTY_STRING>(
    EMPTY_STRING,
  );

  const handleDataSetChange = useCallback(
    (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      setDataSet(event.target.value as DataSetEnum);
    },
    [],
  );

  const handleLoad = useCallback(() => {
    if (!dataSet) {
      return;
    }

    runFixtures(
      { dataSet },
      {
        onSuccess: () => {
          enqueueSnackbar(`Le jeu de données ${dataSet} a bien été chargé.`);
        },
        onError: (error: unknown) => {
          enqueueSnackbar(
            <MessageList messages={userFriendlyErrors(error as BackError)} />,
            {
              variant: 'error',
            },
          );
        },
      },
    );
  }, [dataSet, enqueueSnackbar, runFixtures]);

  return (
    <>
      {isPending ? <Loading /> : null}
      <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
        <TextField
          disabled={disabled || isPending}
          fullWidth
          id="data-set-loading-form-data-set"
          label="Jeu de données"
          onChange={handleDataSetChange}
          select
          value={dataSet}
        >
          {Object.values(DataSetEnum).map((dataSetOption) => (
            <MenuItem key={dataSetOption} value={dataSetOption}>
              {dataSetOption}
            </MenuItem>
          ))}
        </TextField>
        <Button
          disabled={disabled || isPending || !dataSet}
          onClick={handleLoad}
          variant="contained"
        >
          Charger
        </Button>
      </Stack>
    </>
  );
};
